import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCharacters, selectFamilies } from './charactersSlice';
import CharacterCard from './CharacterCard';
import useThemeStyles from '../../hooks/useThemeStyles';

const CharactersListPage = ({ projectId, onCharacterClick, onFamilyClick, onAddCharacter }) => {
  const { colors } = useThemeStyles();
  const allCharacters = useSelector(selectCharacters);
  const allFamilies = useSelector(selectFamilies);
  const characters = useMemo(
    () => allCharacters.filter((c) => c.projectId === projectId),
    [allCharacters, projectId]
  );
  const families = useMemo(
    () => allFamilies.filter((f) => f.projectId === projectId),
    [allFamilies, projectId]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [filterFamily, setFilterFamily] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'age', 'family'

  // Extract roles from tags (common role names)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roleTags = ['Protagonist', 'Supporting', 'Minor', 'Antagonist', 'Deuteragonist'];

  // Get all unique roles from characters
  const allRoles = useMemo(() => {
    const roleSet = new Set();
    characters.forEach((char) => {
      if (char.tags && Array.isArray(char.tags)) {
        char.tags.forEach((tag) => {
          // Check if tag matches a role
          const roleMatch = roleTags.find(
            (role) =>
              tag.toLowerCase().includes(role.toLowerCase()) ||
              role.toLowerCase().includes(tag.toLowerCase())
          );
          if (roleMatch) {
            roleSet.add(roleMatch);
          }
        });
      }
    });
    return Array.from(roleSet).sort();
  }, [characters, roleTags]);

  // Filter and sort characters
  const filteredAndSortedCharacters = useMemo(() => {
    let result = [...characters];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((char) => {
        const searchableText = [
          char.displayName,
          char.fullName,
          char.race,
          char.hairColor,
          char.eyeColor,
          char.sexuality,
          char.personality,
          ...(char.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchableText.includes(query);
      });
    }

    // Family filter
    if (filterFamily !== 'all') {
      result = result.filter((char) => char.familyId === filterFamily);
    }

    // Role filter
    if (filterRole !== 'all') {
      result = result.filter((char) => {
        if (!char.tags || !Array.isArray(char.tags)) return false;
        return char.tags.some((tag) => {
          const tagLower = tag.toLowerCase();
          const roleLower = filterRole.toLowerCase();
          return tagLower.includes(roleLower) || roleLower.includes(tagLower);
        });
      });
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.displayName || '').localeCompare(b.displayName || '');
        case 'age': {
          const ageA = a.age !== null && a.age !== undefined ? a.age : -1;
          const ageB = b.age !== null && b.age !== undefined ? b.age : -1;
          return ageB - ageA; // Descending (oldest first)
        }
        case 'family': {
          const familyA = a.familyId
            ? families.find((f) => f.id === a.familyId)?.familyName || ''
            : '';
          const familyB = b.familyId
            ? families.find((f) => f.id === b.familyId)?.familyName || ''
            : '';
          return familyA.localeCompare(familyB);
        }
        default:
          return 0;
      }
    });

    return result;
  }, [characters, searchQuery, filterFamily, filterRole, sortBy, families]);

  const handleCharacterClick = (character) => {
    if (onCharacterClick) {
      onCharacterClick(character);
    }
  };

  const handleFamilyClick = (family, characterId) => {
    if (onFamilyClick) {
      onFamilyClick(family, characterId);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: colors.background.primary,
      }}
    >
      {/* Header with Title, Count, and Add Button */}
      <Box
        sx={{
          padding: 2,
          borderBottom: `1px solid ${colors.border.subtle}`,
          backgroundColor: colors.paper.background,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: colors.text.primary }}>
              Characters
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              Showing {filteredAndSortedCharacters.length} of {characters.length} characters
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddCharacter}
            sx={{
              backgroundColor: colors.interactive.primary,
              '&:hover': {
                backgroundColor: colors.interactive.hover,
              },
            }}
          >
            + Add Character
          </Button>
        </Box>

        {/* Search and Filters Row */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Search Bar */}
          <TextField
            placeholder="Search characters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: colors.text.secondary }} />,
            }}
            sx={{
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.background.primary,
              },
            }}
          />

          {/* Family Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filterFamily}
              onChange={(e) => setFilterFamily(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: colors.background.primary,
              }}
            >
              <MenuItem value="all">All Families</MenuItem>
              {families.map((family) => (
                <MenuItem key={family.id} value={family.id}>
                  {family.familyName}
                </MenuItem>
              ))}
              {families.length === 0 && (
                <MenuItem value="none" disabled>
                  No families
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Role Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: colors.background.primary,
              }}
            >
              <MenuItem value="all">All Roles</MenuItem>
              {allRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sort */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: colors.background.primary,
              }}
            >
              <MenuItem value="name">Name (A-Z)</MenuItem>
              <MenuItem value="age">Age</MenuItem>
              <MenuItem value="family">Family</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Character List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: 2,
        }}
      >
        {filteredAndSortedCharacters.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              color: colors.text.secondary,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              {characters.length === 0 ? 'No characters yet' : 'No characters match your filters'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {characters.length === 0
                ? 'Click "Add Character" to create your first character'
                : 'Try adjusting your search or filters'}
            </Typography>
            {characters.length === 0 && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={onAddCharacter}>
                Add Character
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            {filteredAndSortedCharacters.map((character) => {
              const family = character.familyId
                ? families.find((f) => f.id === character.familyId)
                : null;
              return (
                <CharacterCard
                  key={character.id}
                  character={character}
                  family={family}
                  onClick={() => handleCharacterClick(character)}
                  onFamilyClick={family ? () => handleFamilyClick(family, character.id) : null}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

CharactersListPage.propTypes = {
  projectId: PropTypes.string.isRequired,
  onCharacterClick: PropTypes.func,
  onFamilyClick: PropTypes.func, // (family, characterId) => void
  onAddCharacter: PropTypes.func,
};

CharactersListPage.defaultProps = {
  onCharacterClick: null,
  onFamilyClick: null,
  onAddCharacter: null,
};

export default CharactersListPage;
